import type { CropArea, RotationAngle } from '@/common/hooks/useImageTransform';
import type { FunctionComponent, ReactNode, RefObject } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { createContext, Suspense, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Crop,
  Download,
  Maximize2,
  Minus,
  Plus,
  RefreshCw,
  RotateCcw,
  RotateCw,
  X,
} from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useImage } from 'react-image';

import { ctw } from '@/common';
import {
  AspectRatio,
  Button,
  buttonVariants,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components';
import { Separator } from '@/components/atoms/Separator';

export type ImageViewerLabels = {
  zoomIn: string;
  zoomOut: string;
  rotateLeft: string;
  rotateRight: string;
  reset: string;
  crop: string;
  applyCrop: string;
  cancelCrop: string;
  download: string;
  fullscreen: string;
  pan: string;
  zoomLevel: string;
  rotation: string;
  dragToSelect: string;
  clickToZoom: string;
};

export const DEFAULT_IMAGE_VIEWER_LABELS: ImageViewerLabels = {
  zoomIn: 'Zoom In',
  zoomOut: 'Zoom Out',
  rotateLeft: 'Rotate Left',
  rotateRight: 'Rotate Right',
  reset: 'Reset',
  crop: 'Crop',
  applyCrop: 'Apply Crop',
  cancelCrop: 'Cancel',
  download: 'Download',
  fullscreen: 'Fullscreen',
  pan: 'Pan',
  zoomLevel: 'Zoom',
  rotation: 'Rotation',
  dragToSelect: 'Drag to select crop area',
  clickToZoom: 'Click and drag to pan, scroll to zoom',
};

export type ImageViewerTransformState = {
  scale: number;
  rotation: RotationAngle;
  position: { x: number; y: number };
  crop: CropArea | undefined;
  isCropping: boolean;
};

export const DEFAULT_TRANSFORM_STATE: ImageViewerTransformState = {
  scale: 1,
  rotation: 0,
  position: { x: 0, y: 0 },
  crop: undefined,
  isCropping: false,
};

export type ImageViewerTransformOptions = {
  minScale?: number;
  maxScale?: number;
  scaleStep?: number;
};

export const DEFAULT_TRANSFORM_OPTIONS: Required<ImageViewerTransformOptions> = {
  minScale: 0.5,
  maxScale: 4,
  scaleStep: 0.25,
};

export type ImageViewerErrorFallbackProps = FallbackProps & {
  className?: string;
  labels?: { error: string; retry: string };
};

const ImageViewerErrorFallback: FunctionComponent<ImageViewerErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  className,
  labels = { error: 'Failed to load image', retry: 'Retry' },
}) => {
  return (
    <figure
      aria-live="polite"
      className={ctw(
        'flex h-full w-full flex-col items-center justify-center gap-3 bg-slate-100 p-4',
        className,
      )}
    >
      <AlertTriangle className="h-8 w-8 text-amber-500" aria-hidden="true" />
      <figcaption className="flex flex-col items-center gap-2">
        <p className="text-sm text-slate-600">{labels.error}</p>
        {error instanceof Error && error.message && (
          <p className="max-w-xs truncate text-xs text-slate-400">{error.message}</p>
        )}
      </figcaption>
      <Button variant="outline" size="sm" onClick={resetErrorBoundary}>
        {labels.retry}
      </Button>
    </figure>
  );
};

export type ImageViewerLoadingFallbackProps = {
  className?: string;
  label?: string;
};

const ImageViewerLoadingFallback: FunctionComponent<ImageViewerLoadingFallbackProps> = ({
  className,
  label = 'Loading image...',
}) => {
  return (
    <figure
      aria-live="polite"
      aria-busy="true"
      className={ctw('flex h-full w-full items-center justify-center bg-slate-100', className)}
    >
      <Skeleton className="h-full w-full" />
      <figcaption className="sr-only">{label}</figcaption>
    </figure>
  );
};

type SuspenseImageProps = {
  src: string;
  alt: string;
  imageRef: RefObject<HTMLImageElement>;
  className?: string;
  style?: React.CSSProperties;
  draggable?: boolean;
};

const SuspenseImage: FunctionComponent<SuspenseImageProps> = ({
  src,
  alt,
  imageRef,
  className,
  style,
  draggable = false,
}) => {
  const { src: loadedSrc } = useImage({
    srcList: src,
    useSuspense: true,
  });

  return (
    <img
      ref={imageRef}
      src={loadedSrc}
      alt={alt}
      className={className}
      style={style}
      draggable={draggable}
    />
  );
};

type ImageViewerContextValue = {
  src: string;
  alt: string;
  imageRef: RefObject<HTMLImageElement>;
  containerRef: RefObject<HTMLDivElement>;
  transformState: ImageViewerTransformState;
  transformOptions: Required<ImageViewerTransformOptions>;
  labels: ImageViewerLabels;
  // Interaction state (internal, for drawing crop)
  cropStart: { x: number; y: number } | null;
  isDrawingCrop: boolean;
  isPanning: boolean;
  // Dialog state
  isDialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  // Computed
  cssTransform: string;
  isTransformed: boolean;
  canZoomIn: boolean;
  canZoomOut: boolean;
  // Callbacks
  onTransformChange: (state: Partial<ImageViewerTransformState>) => void;
  onDownload?: () => void;
  onCropApply?: (croppedImageUrl: string) => void;
  // Internal setters for interaction
  setCropStart: (coords: { x: number; y: number } | null) => void;
  setIsDrawingCrop: (drawing: boolean) => void;
  setIsPanning: (panning: boolean) => void;
  panStartRef: React.MutableRefObject<{ x: number; y: number } | null>;
  // Error boundary reset key for manual retries
  errorBoundaryResetKey: number;
  retryImageLoad: () => void;
};

const ImageViewerContext = createContext<ImageViewerContextValue | null>(null);

const useOptionalImageViewerContext = () => useContext(ImageViewerContext);

const useImageViewerContext = () => {
  const ctx = useContext(ImageViewerContext);

  if (!ctx) {
    throw new Error('ImageViewer sub-components must be used within ImageViewer');
  }

  return ctx;
};

export type ImageViewerProps = {
  src: string;
  alt?: string;
  transformState?: ImageViewerTransformState;
  onTransformChange?: (state: Partial<ImageViewerTransformState>) => void;
  transformOptions?: ImageViewerTransformOptions;
  labels?: Partial<ImageViewerLabels>;
  onDownload?: () => void;
  onCropApply?: (croppedImageUrl: string) => void;
  children: ReactNode;
};

const ImageViewerContainer: FunctionComponent<{
  children: ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <figure
      className={ctw('relative flex flex-col rounded-lg border bg-white shadow-sm', className)}
    >
      {children}
    </figure>
  );
};

const ImageViewerCaption: FunctionComponent<{
  children?: ReactNode;
  className?: string;
  srOnly?: boolean;
}> = ({ children, className, srOnly = false }) => {
  const ctx = useOptionalImageViewerContext();
  const content = children ?? ctx?.alt;

  if (!content) return null;

  return (
    <figcaption
      className={ctw(
        {
          'sr-only': srOnly,
          'p-2 text-center text-sm text-slate-600': !srOnly,
        },
        className,
      )}
    >
      {content}
    </figcaption>
  );
};

type AspectRatioValue = 'auto' | '4/3' | '16/9' | '1/1' | '3/4' | number;

const parseAspectRatio = (aspectRatio: AspectRatioValue): number | undefined => {
  if (aspectRatio === 'auto') return undefined;

  if (typeof aspectRatio === 'number') return aspectRatio;

  const ratioMap: Record<string, number> = {
    '4/3': 4 / 3,
    '16/9': 16 / 9,
    '1/1': 1,
    '3/4': 3 / 4,
  };

  return ratioMap[aspectRatio];
};

const ImageViewerViewport: FunctionComponent<{
  className?: string;
  aspectRatio?: AspectRatioValue;
  renderOverlay?: () => ReactNode;
  renderLoading?: () => ReactNode;
  renderError?: (props: FallbackProps) => ReactNode;
  children?: ReactNode;
}> = ({ className, aspectRatio = 'auto', renderOverlay, renderLoading, renderError, children }) => {
  const ctx = useImageViewerContext();
  const {
    containerRef,
    imageRef,
    src,
    alt,
    transformState,
    cssTransform,
    cropStart,
    isDrawingCrop,
    isPanning,
    labels,
    onTransformChange,
    setCropStart,
    setIsDrawingCrop,
    setIsPanning,
    panStartRef,
    errorBoundaryResetKey,
  } = ctx;

  const getRelativeCoords = useCallback(
    (clientX: number, clientY: number) => {
      if (!imageRef.current) return null;

      const rect = imageRef.current.getBoundingClientRect();

      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
        imageWidth: rect.width,
        imageHeight: rect.height,
      };
    },
    [imageRef],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (transformState.isCropping) {
        const coords = getRelativeCoords(e.clientX, e.clientY);

        if (!coords) return;

        setCropStart(coords);
        setIsDrawingCrop(true);
        onTransformChange({
          crop: { x: coords.x, y: coords.y, width: 0, height: 0, unit: 'px' },
        });

        return;
      }

      if (e.button !== 0) return;

      panStartRef.current = {
        x: e.clientX - transformState.position.x,
        y: e.clientY - transformState.position.y,
      };
      setIsPanning(true);
    },
    [
      transformState.isCropping,
      transformState.position,
      getRelativeCoords,
      setCropStart,
      setIsDrawingCrop,
      setIsPanning,
      onTransformChange,
      panStartRef,
    ],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (transformState.isCropping && isDrawingCrop && cropStart) {
        const coords = getRelativeCoords(e.clientX, e.clientY);

        if (!coords) return;

        const x = Math.min(cropStart.x, coords.x);
        const y = Math.min(cropStart.y, coords.y);
        const width = Math.abs(coords.x - cropStart.x);
        const height = Math.abs(coords.y - cropStart.y);

        onTransformChange({ crop: { x, y, width, height, unit: 'px' } });

        return;
      }

      if (!isPanning || !panStartRef.current) return;

      onTransformChange({
        position: {
          x: e.clientX - panStartRef.current.x,
          y: e.clientY - panStartRef.current.y,
        },
      });
    },
    [
      transformState.isCropping,
      isDrawingCrop,
      cropStart,
      isPanning,
      getRelativeCoords,
      onTransformChange,
      panStartRef,
    ],
  );

  const handleMouseUp = useCallback(() => {
    if (isDrawingCrop) {
      setIsDrawingCrop(false);
    }

    setIsPanning(false);
    panStartRef.current = null;
  }, [isDrawingCrop, setIsDrawingCrop, setIsPanning, panStartRef]);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (transformState.isCropping) return;

      e.preventDefault();
      const delta = e.deltaY > 0 ? -ctx.transformOptions.scaleStep : ctx.transformOptions.scaleStep;
      const newScale = Math.max(
        ctx.transformOptions.minScale,
        Math.min(ctx.transformOptions.maxScale, transformState.scale + delta),
      );
      onTransformChange({ scale: newScale });
    },
    [transformState.isCropping, transformState.scale, ctx.transformOptions, onTransformChange],
  );

  const cropOverlay = useMemo(() => {
    if (!transformState.crop || !imageRef.current || !containerRef.current) return null;

    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    return {
      left: imageRect.left - containerRect.left + transformState.crop.x,
      top: imageRect.top - containerRect.top + transformState.crop.y,
      width: transformState.crop.width,
      height: transformState.crop.height,
    };
  }, [transformState.crop, imageRef, containerRef]);

  const imageStyle: React.CSSProperties = {
    transform: cssTransform,
    transformOrigin: 'center center',
    transition: isPanning ? 'none' : 'transform 0.1s',
  };

  const numericAspectRatio = parseAspectRatio(aspectRatio);

  const viewportContent = (
    <div
      ref={containerRef}
      className={ctw(
        'relative flex h-full w-full items-center justify-center overflow-hidden bg-slate-100',
        {
          'cursor-crosshair': transformState.isCropping,
          'cursor-grab': !transformState.isCropping && !isPanning,
          'cursor-grabbing': isPanning,
        },
        className,
      )}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      <ErrorBoundary
        resetKeys={[src, errorBoundaryResetKey]}
        FallbackComponent={
          renderError
            ? props => <>{renderError(props)}</>
            : props => <ImageViewerErrorFallback {...props} />
        }
      >
        <Suspense fallback={renderLoading?.() ?? <ImageViewerLoadingFallback />}>
          <SuspenseImage
            src={src}
            alt={alt}
            imageRef={imageRef}
            className="max-h-full max-w-full select-none object-contain"
            style={imageStyle}
            draggable={false}
          />
        </Suspense>
      </ErrorBoundary>

      {transformState.isCropping && cropOverlay && (
        <div
          className="pointer-events-none absolute border-2 border-dashed border-blue-500 bg-blue-500/10"
          style={{
            left: cropOverlay.left,
            top: cropOverlay.top,
            width: cropOverlay.width,
            height: cropOverlay.height,
          }}
        />
      )}

      {renderOverlay?.()}

      {transformState.isCropping && !isDrawingCrop && !transformState.crop && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="rounded bg-black/60 px-4 py-2 text-sm text-white">
            {labels.dragToSelect}
          </div>
        </div>
      )}

      {children}
    </div>
  );

  if (numericAspectRatio !== undefined) {
    return <AspectRatio ratio={numericAspectRatio}>{viewportContent}</AspectRatio>;
  }

  return viewportContent;
};

const ImageViewerImage: FunctionComponent<{
  src?: string;
  alt?: string;
  className?: string;
  renderImage?: (props: {
    src: string;
    alt: string;
    imageRef: RefObject<HTMLImageElement>;
    style: React.CSSProperties;
  }) => ReactNode;
}> = ({ src: srcProp, alt: altProp, className, renderImage }) => {
  const ctx = useOptionalImageViewerContext();
  const src = srcProp ?? ctx?.src ?? '';
  const alt = altProp ?? ctx?.alt ?? '';
  const imageRef = ctx?.imageRef;
  const cssTransform = ctx?.cssTransform ?? '';
  const isPanning = ctx?.isPanning ?? false;

  const style: React.CSSProperties = {
    transform: cssTransform,
    transformOrigin: 'center center',
    transition: isPanning ? 'none' : 'transform 0.1s',
  };

  if (renderImage && imageRef) {
    return <>{renderImage({ src, alt, imageRef, style })}</>;
  }

  return (
    <img
      ref={imageRef}
      src={src}
      alt={alt}
      className={ctw('max-h-full max-w-full select-none object-contain', className)}
      style={style}
      draggable={false}
    />
  );
};

const ZoomControls: FunctionComponent<{
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  renderZoomIn?: (props: { onClick: () => void; disabled: boolean }) => ReactNode;
  renderZoomOut?: (props: { onClick: () => void; disabled: boolean }) => ReactNode;
  renderLevel?: (scale: number, minScale: number, maxScale: number) => ReactNode;
}> = ({
  showLabel = false,
  size = 'sm',
  variant = 'ghost',
  renderZoomIn,
  renderZoomOut,
  renderLevel,
}) => {
  const ctx = useImageViewerContext();
  const { transformState, transformOptions, onTransformChange, canZoomIn, canZoomOut, labels } =
    ctx;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const scalePercent = Math.round(transformState.scale * 100);

  const handleZoomIn = useCallback(() => {
    const newScale = Math.min(
      transformOptions.maxScale,
      transformState.scale + transformOptions.scaleStep,
    );
    onTransformChange({ scale: newScale });
  }, [transformState.scale, transformOptions, onTransformChange]);

  const handleZoomOut = useCallback(() => {
    const newScale = Math.max(
      transformOptions.minScale,
      transformState.scale - transformOptions.scaleStep,
    );
    onTransformChange({ scale: newScale });
  }, [transformState.scale, transformOptions, onTransformChange]);

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1">
        {renderZoomOut ? (
          renderZoomOut({ onClick: handleZoomOut, disabled: !canZoomOut })
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={variant}
                size="icon"
                className={sizeClasses[size]}
                onClick={handleZoomOut}
                disabled={!canZoomOut}
              >
                <Minus className={iconSizes[size]} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{labels.zoomOut}</TooltipContent>
          </Tooltip>
        )}

        {renderLevel ? (
          renderLevel(transformState.scale, transformOptions.minScale, transformOptions.maxScale)
        ) : showLabel ? (
          <span className="min-w-[3rem] text-center text-sm font-medium">{scalePercent}%</span>
        ) : null}

        {renderZoomIn ? (
          renderZoomIn({ onClick: handleZoomIn, disabled: !canZoomIn })
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={variant}
                size="icon"
                className={sizeClasses[size]}
                onClick={handleZoomIn}
                disabled={!canZoomIn}
              >
                <Plus className={iconSizes[size]} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{labels.zoomIn}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

const RotationControls: FunctionComponent<{
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  renderRotateLeft?: (props: { onClick: () => void }) => ReactNode;
  renderRotateRight?: (props: { onClick: () => void }) => ReactNode;
  renderAngle?: (rotation: number) => ReactNode;
}> = ({
  showLabel = false,
  size = 'sm',
  variant = 'ghost',
  renderRotateLeft,
  renderRotateRight,
  renderAngle,
}) => {
  const ctx = useImageViewerContext();
  const { transformState, onTransformChange, labels } = ctx;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const handleRotateLeft = useCallback(() => {
    const newRotation = ((transformState.rotation - 90 + 360) % 360) as RotationAngle;
    onTransformChange({ rotation: newRotation });
  }, [transformState.rotation, onTransformChange]);

  const handleRotateRight = useCallback(() => {
    const newRotation = ((transformState.rotation + 90) % 360) as RotationAngle;
    onTransformChange({ rotation: newRotation });
  }, [transformState.rotation, onTransformChange]);

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1">
        {renderRotateLeft ? (
          renderRotateLeft({ onClick: handleRotateLeft })
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={variant}
                size="icon"
                className={sizeClasses[size]}
                onClick={handleRotateLeft}
              >
                <RotateCcw className={iconSizes[size]} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{labels.rotateLeft}</TooltipContent>
          </Tooltip>
        )}

        {renderAngle ? (
          renderAngle(transformState.rotation)
        ) : showLabel ? (
          <span className="min-w-[3rem] text-center text-sm font-medium">
            {transformState.rotation}°
          </span>
        ) : null}

        {renderRotateRight ? (
          renderRotateRight({ onClick: handleRotateRight })
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={variant}
                size="icon"
                className={sizeClasses[size]}
                onClick={handleRotateRight}
              >
                <RotateCw className={iconSizes[size]} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{labels.rotateRight}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

const CropControls: FunctionComponent<{
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  renderCropButton?: (props: { onClick: () => void; isCropping: boolean }) => ReactNode;
  renderApplyButton?: (props: { onClick: () => void; disabled: boolean }) => ReactNode;
  renderCancelButton?: (props: { onClick: () => void }) => ReactNode;
}> = ({
  size = 'sm',
  variant = 'ghost',
  renderCropButton,
  renderApplyButton,
  renderCancelButton,
}) => {
  const ctx = useImageViewerContext();
  const { transformState, onTransformChange, imageRef, labels, onCropApply } = ctx;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const handleStartCrop = useCallback(() => {
    onTransformChange({ isCropping: true, crop: undefined });
  }, [onTransformChange]);

  const handleCancelCrop = useCallback(() => {
    onTransformChange({ isCropping: false, crop: undefined });
  }, [onTransformChange]);

  const handleApplyCrop = useCallback(async () => {
    if (!transformState.crop || !imageRef.current) return;

    const image = imageRef.current;

    if (!image.complete || image.naturalWidth === 0) return;

    const { rotation } = transformState;
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    const rect = image.getBoundingClientRect();

    const isRotated90or270 = rotation === 90 || rotation === 270;
    const rotatedWidth = isRotated90or270 ? naturalHeight : naturalWidth;
    const rotatedHeight = isRotated90or270 ? naturalWidth : naturalHeight;

    const rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = rotatedWidth;
    rotatedCanvas.height = rotatedHeight;

    const rotatedCtx = rotatedCanvas.getContext('2d');

    if (!rotatedCtx) return;

    rotatedCtx.save();
    rotatedCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
    rotatedCtx.rotate((rotation * Math.PI) / 180);
    rotatedCtx.drawImage(image, -naturalWidth / 2, -naturalHeight / 2);
    rotatedCtx.restore();

    const scaleX = rotatedWidth / rect.width;
    const scaleY = rotatedHeight / rect.height;

    const sourceX = transformState.crop.x * scaleX;
    const sourceY = transformState.crop.y * scaleY;
    const sourceWidth = transformState.crop.width * scaleX;
    const sourceHeight = transformState.crop.height * scaleY;

    const pixelRatio = window.devicePixelRatio;
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = transformState.crop.width * pixelRatio;
    outputCanvas.height = transformState.crop.height * pixelRatio;

    const outputCtx = outputCanvas.getContext('2d');

    if (!outputCtx) return;

    outputCtx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    outputCtx.imageSmoothingQuality = 'high';

    outputCtx.drawImage(
      rotatedCanvas,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      transformState.crop.width,
      transformState.crop.height,
    );

    try {
      const result = outputCanvas.toDataURL('image/png');
      onCropApply?.(result);
      onTransformChange({
        isCropping: false,
        crop: undefined,
        scale: 1,
        rotation: 0,
        position: { x: 0, y: 0 },
      });
    } catch (e) {
      console.error('Failed to export canvas (CORS issue?)', e);
    }
  }, [transformState, imageRef, onCropApply, onTransformChange]);

  const hasCrop =
    transformState.crop !== undefined &&
    transformState.crop.width > 0 &&
    transformState.crop.height > 0;

  if (transformState.isCropping) {
    return (
      <TooltipProvider>
        <div className="flex items-center gap-1">
          {renderCancelButton ? (
            renderCancelButton({ onClick: handleCancelCrop })
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={variant}
                  size="icon"
                  className={sizeClasses[size]}
                  onClick={handleCancelCrop}
                >
                  <X className={iconSizes[size]} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{labels.cancelCrop}</TooltipContent>
            </Tooltip>
          )}

          {renderApplyButton ? (
            renderApplyButton({ onClick: () => void handleApplyCrop(), disabled: !hasCrop })
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={variant}
                  size="icon"
                  className={ctw(sizeClasses[size], 'text-green-600 hover:text-green-700')}
                  onClick={() => void handleApplyCrop()}
                  disabled={!hasCrop}
                >
                  <Check className={iconSizes[size]} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{labels.applyCrop}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1">
        {renderCropButton ? (
          renderCropButton({ onClick: handleStartCrop, isCropping: false })
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={variant}
                size="icon"
                className={sizeClasses[size]}
                onClick={handleStartCrop}
              >
                <Crop className={iconSizes[size]} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{labels.crop}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

const ResetButton: FunctionComponent<{
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  showLabel?: boolean;
  render?: (props: { onClick: () => void; disabled: boolean }) => ReactNode;
}> = ({ size = 'sm', variant = 'ghost', showLabel = false, render }) => {
  const ctx = useImageViewerContext();
  const { onTransformChange, isTransformed, labels } = ctx;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const handleReset = useCallback(() => {
    onTransformChange({
      scale: 1,
      rotation: 0,
      position: { x: 0, y: 0 },
      crop: undefined,
      isCropping: false,
    });
  }, [onTransformChange]);

  if (render) {
    return <>{render({ onClick: handleReset, disabled: !isTransformed })}</>;
  }

  if (showLabel) {
    return (
      <Button
        variant={variant}
        size="sm"
        onClick={handleReset}
        disabled={!isTransformed}
        className="gap-1"
      >
        <RefreshCw className={iconSizes[size]} />
        {labels.reset}
      </Button>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={variant}
            size="icon"
            className={sizeClasses[size]}
            onClick={handleReset}
            disabled={!isTransformed}
          >
            <RefreshCw className={iconSizes[size]} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{labels.reset}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const DownloadButton: FunctionComponent<{
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  showLabel?: boolean;
  render?: (props: { href: string; download: string }) => ReactNode;
}> = ({ size = 'sm', variant = 'ghost', showLabel = false, render }) => {
  const ctx = useImageViewerContext();
  const { onDownload, labels, src, alt } = ctx;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const downloadFilename = alt || 'image';

  const isCrossOrigin = useMemo(() => {
    try {
      const url = new URL(src, window.location.href);

      return url.origin !== window.location.origin;
    } catch {
      // If URL parsing fails, assume same-origin (relative URL)
      return false;
    }
  }, [src]);

  const handleDownload = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onDownload) {
        e.preventDefault();
        onDownload();

        return;
      }

      // For cross-origin images, browsers ignore the download attribute
      // We need to fetch as blob and trigger download programmatically
      if (isCrossOrigin) {
        e.preventDefault();

        try {
          const response = await fetch(src);
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = downloadFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(blobUrl);
        } catch (error) {
          console.error('Failed to download image:', error);
        }
      }
      // For same-origin images, let the browser handle the download attribute naturally
    },
    [onDownload, isCrossOrigin, src, downloadFilename],
  );

  if (render) {
    return <>{render({ href: src, download: downloadFilename })}</>;
  }

  if (showLabel) {
    return (
      <a
        href={src}
        download={downloadFilename}
        onClick={handleDownload}
        className={ctw(buttonVariants({ variant, size: 'sm' }), 'gap-1')}
        title={labels.download}
      >
        <Download className={iconSizes[size]} />
        {labels.download}
      </a>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={src}
            download={downloadFilename}
            onClick={handleDownload}
            className={ctw(buttonVariants({ variant, size: 'icon' }), sizeClasses[size])}
          >
            <Download className={iconSizes[size]} />
          </a>
        </TooltipTrigger>
        <TooltipContent>{labels.download}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const FullscreenButton: FunctionComponent<{
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  showLabel?: boolean;
  render?: (props: { onClick: () => void; isOpen: boolean }) => ReactNode;
}> = ({ size = 'sm', variant = 'ghost', showLabel = false, render }) => {
  const ctx = useImageViewerContext();
  const { labels, isDialogOpen, setDialogOpen } = ctx;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const handleClick = () => setDialogOpen(true);

  if (render) {
    return <>{render({ onClick: handleClick, isOpen: isDialogOpen })}</>;
  }

  if (showLabel) {
    return (
      <Button
        variant={variant}
        size="sm"
        onClick={handleClick}
        className="gap-1"
        title={labels.fullscreen}
      >
        <Maximize2 className={iconSizes[size]} />
        {labels.fullscreen}
      </Button>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={variant}
            size="icon"
            onClick={handleClick}
            className={sizeClasses[size]}
            title={labels.fullscreen}
          >
            <Maximize2 className={iconSizes[size]} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{labels.fullscreen}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const Toolbar: FunctionComponent<{
  position?: 'top' | 'bottom' | 'floating-top' | 'floating-bottom';
  className?: string;
  children?: ReactNode;
}> = ({ position = 'bottom', className, children }) => {
  const positionClasses = {
    top: 'border-b p-2',
    bottom: 'border-t p-2',
    'floating-top':
      'absolute top-4 left-1/2 -translate-x-1/2 rounded-lg bg-white/90 p-2 shadow-lg backdrop-blur-sm',
    'floating-bottom':
      'absolute bottom-4 left-1/2 -translate-x-1/2 rounded-lg bg-white/90 p-2 shadow-lg backdrop-blur-sm',
  };

  return (
    <div
      className={ctw(
        'flex items-center justify-center gap-2',
        positionClasses[position],
        className,
      )}
    >
      {children || (
        <TooltipProvider>
          <ZoomControls showLabel />
          <Separator orientation="vertical" className="h-6" />
          <RotationControls />
          <Separator orientation="vertical" className="h-6" />
          <CropControls />
          <Separator orientation="vertical" className="h-6" />
          <ResetButton />
          <DownloadButton />
        </TooltipProvider>
      )}
    </div>
  );
};

const Divider: FunctionComponent<{
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}> = ({ orientation = 'vertical', className }) => {
  return (
    <Separator
      orientation={orientation === 'vertical' ? 'vertical' : 'horizontal'}
      className={ctw(
        {
          'h-6': orientation === 'vertical',
        },
        className,
      )}
    />
  );
};

const RetryButton: FunctionComponent<{
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  showLabel?: boolean;
  render?: (props: { onClick: () => void }) => ReactNode;
}> = ({ size = 'sm', variant = 'outline', showLabel = true, render }) => {
  const ctx = useImageViewerContext();
  const { retryImageLoad } = ctx;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  if (render) {
    return <>{render({ onClick: retryImageLoad })}</>;
  }

  if (showLabel) {
    return (
      <Button variant={variant} size="sm" onClick={retryImageLoad} className="gap-1">
        <RefreshCw className={iconSizes[size]} />
        Retry
      </Button>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={variant}
            size="icon"
            className={sizeClasses[size]}
            onClick={retryImageLoad}
          >
            <RefreshCw className={iconSizes[size]} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Retry loading image</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const Status: FunctionComponent<{
  showZoom?: boolean;
  showRotation?: boolean;
  showCropStatus?: boolean;
  className?: string;
  render?: (status: {
    scale: number;
    rotation: number;
    isCropping: boolean;
    isTransformed: boolean;
    isPanning: boolean;
  }) => ReactNode;
  children?: ReactNode;
}> = ({
  showZoom = true,
  showRotation = true,
  showCropStatus = true,
  className,
  render,
  children,
}) => {
  const ctx = useImageViewerContext();
  const { transformState, isTransformed, isPanning } = ctx;

  const status = {
    scale: transformState.scale,
    rotation: transformState.rotation,
    isCropping: transformState.isCropping,
    isTransformed,
    isPanning,
  };

  if (render) {
    return <>{render(status)}</>;
  }

  if (children) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={ctw('flex items-center gap-4 text-xs text-slate-500', className)}>
      {showZoom && <span>Zoom: {Math.round(status.scale * 100)}%</span>}
      {showRotation && <span>Rotation: {status.rotation}°</span>}
      {showCropStatus && status.isCropping && (
        <span className="text-blue-600">Cropping mode active</span>
      )}
    </div>
  );
};

const CropOverlay: FunctionComponent<{
  className?: string;
  render?: (
    crop: CropArea | null,
    overlayPosition: { left: number; top: number; width: number; height: number } | null,
  ) => ReactNode;
}> = ({ className, render }) => {
  const ctx = useImageViewerContext();
  const { transformState, imageRef, containerRef } = ctx;

  const cropOverlay = useMemo(() => {
    if (!transformState.crop || !imageRef.current || !containerRef.current) return null;

    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    return {
      left: imageRect.left - containerRect.left + transformState.crop.x,
      top: imageRect.top - containerRect.top + transformState.crop.y,
      width: transformState.crop.width,
      height: transformState.crop.height,
    };
  }, [transformState.crop, imageRef, containerRef]);

  if (render) {
    return <>{render(transformState.crop ?? null, cropOverlay)}</>;
  }

  if (!transformState.isCropping || !cropOverlay) {
    return null;
  }

  return (
    <div
      className={ctw(
        'pointer-events-none absolute border-2 border-dashed border-blue-500 bg-blue-500/10',
        className,
      )}
      style={{
        left: cropOverlay.left,
        top: cropOverlay.top,
        width: cropOverlay.width,
        height: cropOverlay.height,
      }}
    />
  );
};

export const ImageViewer: FunctionComponent<ImageViewerProps> & {
  Container: typeof ImageViewerContainer;
  Caption: typeof ImageViewerCaption;
  Viewport: typeof ImageViewerViewport;
  Image: typeof ImageViewerImage;
  Toolbar: typeof Toolbar;
  ZoomControls: typeof ZoomControls;
  RotationControls: typeof RotationControls;
  CropControls: typeof CropControls;
  ResetButton: typeof ResetButton;
  DownloadButton: typeof DownloadButton;
  FullscreenButton: typeof FullscreenButton;
  RetryButton: typeof RetryButton;
  Divider: typeof Divider;
  Status: typeof Status;
  CropOverlay: typeof CropOverlay;
  ErrorFallback: typeof ImageViewerErrorFallback;
  LoadingFallback: typeof ImageViewerLoadingFallback;
  FullscreenDialog: typeof ImageViewerFullscreenDialog;
} = ({
  src,
  alt = '',
  transformState: transformStateProp,
  onTransformChange: onTransformChangeProp,
  transformOptions: transformOptionsProp,
  labels: labelsProp,
  onDownload,
  onCropApply,
  children,
}) => {
  const labels = { ...DEFAULT_IMAGE_VIEWER_LABELS, ...labelsProp };
  const transformOptions = { ...DEFAULT_TRANSFORM_OPTIONS, ...transformOptionsProp };

  // Internal state for uncontrolled mode
  const [internalState, setInternalState] = useState<ImageViewerTransformState>(
    transformStateProp ?? DEFAULT_TRANSFORM_STATE,
  );

  // Use controlled state if provided, otherwise use internal
  const transformState = transformStateProp ?? internalState;
  const onTransformChange = useCallback(
    (partial: Partial<ImageViewerTransformState>) => {
      if (onTransformChangeProp) {
        onTransformChangeProp(partial);
      } else {
        setInternalState(prev => ({ ...prev, ...partial }));
      }
    },
    [onTransformChangeProp],
  );

  // Interaction state (always internal - ephemeral UI state)
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [isDrawingCrop, setIsDrawingCrop] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number } | null>(null);

  // Error boundary reset key for manual retries
  const [errorBoundaryResetKey, setErrorBoundaryResetKey] = useState(0);
  const retryImageLoad = useCallback(() => {
    setErrorBoundaryResetKey(prev => prev + 1);
  }, []);

  // Dialog state
  const [isDialogOpen, setDialogOpen] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const cssTransform = useMemo(() => {
    const { scale, rotation, position } = transformState;

    return `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg) scale(${scale})`;
  }, [transformState]);

  const isTransformed = useMemo(
    () =>
      transformState.scale !== 1 ||
      transformState.rotation !== 0 ||
      transformState.position.x !== 0 ||
      transformState.position.y !== 0,
    [transformState],
  );

  const canZoomIn = transformState.scale < transformOptions.maxScale;
  const canZoomOut = transformState.scale > transformOptions.minScale;

  const ctxValue: ImageViewerContextValue = {
    src,
    alt,
    imageRef,
    containerRef,
    transformState,
    transformOptions,
    labels,
    cropStart,
    isDrawingCrop,
    isPanning,
    isDialogOpen,
    setDialogOpen,
    cssTransform,
    isTransformed,
    canZoomIn,
    canZoomOut,
    onTransformChange,
    onDownload,
    onCropApply,
    setCropStart,
    setIsDrawingCrop,
    setIsPanning,
    panStartRef,
    errorBoundaryResetKey,
    retryImageLoad,
  };

  return <ImageViewerContext.Provider value={ctxValue}>{children}</ImageViewerContext.Provider>;
};

const ImageViewerFullscreenDialog: FunctionComponent<{
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}> = ({
  open: openProp,
  onOpenChange: onOpenChangeProp,
  title,
  description,
  children,
  className,
  contentClassName,
}) => {
  const ctx = useOptionalImageViewerContext();

  // Use controlled props if provided, otherwise use context state
  const isOpen = openProp ?? ctx?.isDialogOpen ?? false;
  const handleOpenChange = onOpenChangeProp ?? ctx?.setDialogOpen ?? (() => {});

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={ctw(
          'flex h-full max-h-[95vh] w-full !max-w-[95vw] flex-col p-0 sm:!max-w-[95vw]',
          contentClassName,
          className,
        )}
      >
        {(title || description) && (
          <DialogHeader className="flex-shrink-0 px-6 pb-0 pt-6">
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
        )}
        <div className="min-h-0 flex-1 overflow-auto p-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
};

ImageViewer.Container = ImageViewerContainer;
ImageViewer.Caption = ImageViewerCaption;
ImageViewer.Viewport = ImageViewerViewport;
ImageViewer.Image = ImageViewerImage;
ImageViewer.Toolbar = Toolbar;
ImageViewer.ZoomControls = ZoomControls;
ImageViewer.RotationControls = RotationControls;
ImageViewer.CropControls = CropControls;
ImageViewer.ResetButton = ResetButton;
ImageViewer.DownloadButton = DownloadButton;
ImageViewer.FullscreenButton = FullscreenButton;
ImageViewer.RetryButton = RetryButton;
ImageViewer.Divider = Divider;
ImageViewer.Status = Status;
ImageViewer.CropOverlay = CropOverlay;
ImageViewer.ErrorFallback = ImageViewerErrorFallback;
ImageViewer.LoadingFallback = ImageViewerLoadingFallback;
ImageViewer.FullscreenDialog = ImageViewerFullscreenDialog;
